'use client';

import { useCallback } from 'react';
import { saveResultsPerPagePreference } from '../../utils/resultsPerPageStorage';
import {
  useSearchControllers,
  useSearchEngine,
} from '../headless/SearchDefinitionContext';
import { registerNumberOfResultsAsDefault } from '../headless/pagination';

/**
 * Page-size state + selection for the SSR arm, shared by the desktop control
 * and the mobile drawer so a choice made on either surface hits the same
 * engine, keeps `numberOfResults` out of the URL, and persists identically.
 *
 * `state`, `methods` and `engine` are exposed for the desktop control's
 * saved-preference reconciliation, which is deliberately owned by that single
 * mount rather than re-run by every surface that offers a page size.
 */
export const useSearchV2ResultsPerPage = () => {
  const controllers = useSearchControllers();
  const { state, methods } = controllers.useResultsPerPage();
  const engine = useSearchEngine();

  const selectResultsPerPage = useCallback(
    (numberOfResults: number) => {
      methods?.set(numberOfResults);
      registerNumberOfResultsAsDefault(engine, numberOfResults);
      saveResultsPerPagePreference(numberOfResults);
    },
    [methods, engine]
  );

  return { state, methods, engine, selectResultsPerPage };
};
